/**
 * MaskInput Molecule
 *
 * An input with mask support for formatted data entry (phone, CPF, date, etc).
 * Combines Input atom with react-input-mask library.
 */

import * as React from 'react';
import InputMask, { Props as InputMaskProps } from 'react-input-mask';

import { Skeleton } from '@/components/atoms/feedback';
import { cn } from '@/lib/utils';
import { twMerge } from 'tailwind-merge';

export interface MaskInputProps extends InputMaskProps {
  label?: string;
  description?: string;
  isLoading?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
}

const MaskInput = React.forwardRef<HTMLInputElement, MaskInputProps>(
  ({ className, type, label, description, mask = [], isLoading, ...props }, ref) => {
    const generatedId = React.useId();
    const inputId = props.id || generatedId;
    return (
      <div className='flex w-full flex-col'>
        {Boolean(label) && (
          <label htmlFor={inputId} className='visible mb-2 block opacity-100'>
            {label}
          </label>
        )}
        <InputMask
          id={inputId}
          className={cn(
            'peer flex h-11 w-full border border-gray2 bg-transparent p-3 ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-zinc-500 invalid:border-2 invalid:border-primary-600 focus-visible:border-zinc-700 focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-zinc-300 disabled:opacity-25 dark:border-zinc-800 dark:bg-zinc-950',
            className
          )}
          inputRef={ref}
          mask={mask}
          type={isLoading ? 'hidden' : type}
          {...props}
        />
        {isLoading && <Skeleton className={twMerge('h-11 w-full', className)} />}
        {description && <p className='text-xs text-slate-500 peer-invalid:text-primary-600'>{description}</p>}
      </div>
    );
  }
);
MaskInput.displayName = 'MaskInput';

export { MaskInput };
